import { Button, ButtonStyles, Table, TableColumn, Validation } from '@silverfort/sf-components';
import { UsersBoundLocalAccount } from '../../../types/User';
import { LOCAL_ACCOUNT_TYPE_TO_LABEL } from '../../LocalAccounts/consts';
import { usePopup } from '../../../context/PopupProvider';
import { FC } from 'react';

const ROW_HEIGHT = 48;

const BOUND_LOCAL_ACCOUNTS_TABLE_COLUMNS: TableColumn<UsersBoundLocalAccount>[] = [
    {
        columnKey: 'userName',
        title: 'Name',
        render: (row) => row?.userName
    },
    {
        columnKey: 'hostname',
        title: 'Device name',
        render: (row) => row?.hostname
    },
    {
        columnKey: 'accountType',
        title: 'Account type',
        render: (row) => LOCAL_ACCOUNT_TYPE_TO_LABEL[row.accountType]
    }
];

interface UseBoundLocalAccountsPopupParams {
    localAccounts: UsersBoundLocalAccount[];
    localAccountsCount: number;
}

interface UseBoundLocalAccountsPopupReturnType {
    openBoundLocalAccountsPopup: () => void;
}

const BoundLocalAccountsPopupContent: FC<{ localAccounts: UsersBoundLocalAccount[] }> = ({ localAccounts }) => {
    return (
        <Table
            columns={BOUND_LOCAL_ACCOUNTS_TABLE_COLUMNS}
            rows={localAccounts}
            tableContainerClassName="w-[792px] h-fit max-h-[calc(100vh-200px)] overflow-y-auto"
            bodyHeight={localAccounts.length * ROW_HEIGHT}
            keyExtractor={(row) => row.guid}
        />
    );
};

const FooterComponent: FC<{
    handleClose: () => void;
    localAccountsCount: number;
    localAccountsLimit: number;
}> = ({ handleClose, localAccountsCount, localAccountsLimit }) => {
    const localAccountsLimitExceeded = localAccountsCount > localAccountsLimit;

    return (
        <div className={`flex ${localAccountsLimitExceeded ? 'justify-between' : 'justify-end'} items-center w-full`}>
            {localAccountsLimitExceeded && (
                <Validation
                    showIcon
                    text={`Only the first ${localAccountsLimit} bound local accounts are displayed`}
                    className={`transition-opacity ${localAccountsLimitExceeded ? 'opacity-100' : 'opacity-0'}`}
                />
            )}
            <Button text="Done" buttonStyle={ButtonStyles.PrimarySolid} onClick={handleClose} />
        </div>
    );
};

export const useBoundLocalAccountsPopup = ({
    localAccounts,
    localAccountsCount
}: UseBoundLocalAccountsPopupParams): UseBoundLocalAccountsPopupReturnType => {
    const { openPopup, closePopup } = usePopup();

    return {
        openBoundLocalAccountsPopup: () =>
            openPopup({
                title: 'Local accounts',
                ContentComponent: BoundLocalAccountsPopupContent,
                ContentComponentProps: { localAccounts },
                FooterComponent: FooterComponent,
                FooterComponentProps: {
                    handleClose: closePopup,
                    localAccountsCount,
                    localAccountsLimit: localAccounts.length
                },
                popupClassName: 'max-w-[824px]',
                contentClassName: 'px-4'
            })
    };
};
